import React, { useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useCartStore } from "@/stores/cartStore";

interface AppInitializerProps {
  children: React.ReactNode;
}

/**
 * Componente que inicializa la autenticación al cargar la aplicación
 *
 * Verifica si hay una sesión válida en localStorage y la restaura
 * si es válida, o limpia la sesión si no lo es.
 * También carga el carrito del usuario si está autenticado.
 * Optimizado con tema global y estados de carga mejorados.
 */
const AppInitializer: React.FC<AppInitializerProps> = ({ children }) => {
  const { isInitializing, initializeAuth } = useAuth();
  const { loadCart, setAnonymousSessionToken, setIsAnonymous } = useCartStore();
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Solo inicializar una vez al cargar la aplicación
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      initializeAuth();
    }
  }, [initializeAuth]);

  // Cargar el carrito (con delay para evitar conflictos post-pago)
  useEffect(() => {
    // Delay para evitar cargar carrito inmediatamente después de un pago
    const timer = setTimeout(() => {
      console.log("🔄 Cargando carrito desde AppInitializer...");
      loadCart();
    }, 1000); // 1 segundo de delay

    return () => clearTimeout(timer);
  }, [loadCart]);

  // set anonymous session token from local storage
  useEffect(() => {
    const anonymousSessionToken = localStorage.getItem("anonymousSessionToken");
    if (anonymousSessionToken) {
      setAnonymousSessionToken(anonymousSessionToken);
      setIsAnonymous(true);
    }
  }, []);

  // Mostrar loading mientras se inicializa la autenticación
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-sm mx-auto">
          {/* Logo/Icono de la aplicación */}
          <div className="mb-6">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <div className="w-8 h-8 bg-primary rounded-md"></div>
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Chilenito SPA
            </h1>
            <p className="text-sm text-muted-foreground">Sistema de Gestión</p>
          </div>

          {/* Spinner de carga */}
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <span className="text-foreground font-medium">
              Inicializando aplicación...
            </span>
          </div>

          {/* Información adicional */}
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <div className="w-3 h-3 bg-muted-foreground rounded-full"></div>
            <span>Verificando autenticación</span>
          </div>

          {/* Barra de progreso visual */}
          <div className="mt-6 w-full bg-muted rounded-full h-1 overflow-hidden">
            <div
              className="h-full bg-primary rounded-full animate-pulse"
              style={{ width: "60%" }}
            ></div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AppInitializer;
